import React, { useState, useEffect } from 'react';
import styles from './TutorialIntro.module.css';

interface TutorialIntroProps {
  isVisible: boolean;
  constellation: string;
  tutorialIndex: number;
  tutorialTitle: string;
  pieceType: string;
  onClose: () => void;
}

interface TutorialIntroData {
  goal: string;
  description: string;
  tips: string[];
}

// Tutorial data for each piece type and specific tutorials
const TUTORIAL_DATA: Record<string, Record<number, TutorialIntroData>> = {
  bear: {
    0: { // Bear Tutorial 1 (index 0)
      goal: "Welcome to your first tutorial!",
      description: "Let's start off easy - click on the bear piece to select it",
      tips: [
        "Move anywhere 2 times to complete the tutorial",
        "When you select the bear it will highlight where it can move to",
        "To deselect click anywhere else!"
      ]
    },
    1: { // Bear Tutorial 2 (index 1) - Race Victory
      goal: "Race Victory",
      description: "In Zodiac chess a king piece can win by simply moving to the other side of the board!",
      tips: [
        "Navigate your bear around this enemy without being captured to complete the tutorial!"
      ]
    },
    2: { // Bear Tutorial 3 (index 2) - Predator
      goal: "Predator",
      description: "The bear is a predator piece, this means it cannot be captured by many pieces including crane, penguin and goat!",
      tips: [
        "This tutorial should be a walk in the park to race victory against these unthreatening opponents!"
      ]
    },
    3: { // Bear Tutorial 4 (index 3) - King Capture
      goal: "King Capture",
      description: "You can also win by capturing the enemy king piece!",
      tips: [
        "Just as you can win by race, you will need to stop this enemy crane king from reaching your backrank and capture it before it sneaks by!",
        "Pro-tip: Click on any enemy piece to see their movement pattern as well!"
      ]
    },
    4: { // Bear Tutorial 5 (index 4) - Bear Challenge
      goal: "Bear challenge",
      description: "Defeat the opposing Rhino's in this unlimited move Bear Challenge!",
      tips: []
    }
  },
  crane: {
    0: { // Crane Tutorial 1 (index 0)
      goal: "Crane movement",
      description: "Experiment with the cranes advanced movement and try to navigate around this enemy crane while stopping them from race-victory!",
      tips: []
    },
    1: { // Crane Tutorial 2 (index 1)
      goal: "Bird type",
      description: "The crane is a very simple piece with its main advantage being its high level of mobility! The crane can move 1 or 2 in any straight line including flying over other pieces!",
      tips: [
        "It's one downside is it cannot capture pieces like: bear, rhino, tree or wolf!",
        "Navigate your way through these ferocious bears to a race victory!"
      ]
    },
    2: { // Crane Tutorial 3 (index 2)
      goal: "Crane challenge",
      description: "Test out your crane knowledge against this cohesive pack of wolves in an open, 18 turn limit challenge!",
      tips: []
    }
  },
  frog: {
    0: {
      goal: "Placeholder goal for Frog tutorial",
      description: "Placeholder description for Frog piece mechanics and strategy", 
      tips: ["Placeholder tip 1", "Placeholder tip 2", "Placeholder tip 3"]
    }
  },
  ox: {
    0: {
      goal: "Placeholder goal for Ox tutorial",
      description: "Placeholder description for Ox piece mechanics and strategy",
      tips: ["Placeholder tip 1", "Placeholder tip 2", "Placeholder tip 3"]
    }
  },
  rabbit: {
    0: {
      goal: "Placeholder goal for Rabbit tutorial",
      description: "Placeholder description for Rabbit piece mechanics and strategy",
      tips: ["Placeholder tip 1", "Placeholder tip 2", "Placeholder tip 3"]
    }
  },
  snake: {
    0: {
      goal: "Placeholder goal for Snake tutorial",
      description: "Placeholder description for Snake piece mechanics and strategy",
      tips: ["Placeholder tip 1", "Placeholder tip 2", "Placeholder tip 3"]
    }
  },
  tree: {
    0: {
      goal: "Tree movement",
      description: "Learn to moves as the tree piece to acheive a race victory!",
      tips: []
    },
    1: {
      goal: "Tree Housing",
      description: "A tree piece can house other pieces and protect them! When your crane king is housed in your tree, the wolves cannot capture the crane up the tree OR the defended tree, a powerful synergy!",
      tips: []
    }
  }
};

const TUTORIAL_INTRO_DISABLED_KEY = 'tutorialIntroDisabled';
const TUTORIAL_INTRO_SEEN_KEY = 'tutorialIntroSeen';

function TutorialIntro({
  isVisible,
  constellation,
  tutorialIndex,
  tutorialTitle,
  pieceType,
  onClose
}: TutorialIntroProps) {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  
  useEffect(() => {
    // Check if user has disabled tutorial intros
    const isDisabled = localStorage.getItem(TUTORIAL_INTRO_DISABLED_KEY) === 'true';
    if (isDisabled && isVisible) {
      onClose();
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  const tutorialKey = `${constellation}-${tutorialIndex}`;
  const seenKey = `${TUTORIAL_INTRO_SEEN_KEY}-${tutorialKey}`;

  // Check if this specific tutorial intro has been seen BEFORE rendering anything
  const hasBeenSeen = localStorage.getItem(seenKey) === 'true';
  if (hasBeenSeen) {
    // Don't render anything, just close immediately to prevent overlay flash
    // Use setTimeout to avoid calling onClose during render
    setTimeout(() => onClose(), 0);
    return null;
  }

  const tutorialData = TUTORIAL_DATA[pieceType.toLowerCase()]?.[tutorialIndex] || 
                      TUTORIAL_DATA.bear?.[0] || 
                      { goal: "Tutorial goal", description: "Tutorial description", tips: ["Tip 1"] };

  const handleClose = () => {
    // Mark this tutorial intro as seen
    localStorage.setItem(seenKey, 'true');
    
    // If "don't show again" is checked, disable all future tutorial intros
    if (dontShowAgain) {
      localStorage.setItem(TUTORIAL_INTRO_DISABLED_KEY, 'true');
    }
    
    onClose();
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.popup}>
        <div className={styles.header}>
          <h2 className={styles.title}>{tutorialData.goal}</h2>
        </div>
        
        <div className={styles.content}>
          <p className={styles.description}>{tutorialData.description}</p>
          {tutorialData.tips.map((tip, index) => (
            <p key={index} className={styles.tip}>{tip}</p>
          ))}
        </div>
        
        <div className={styles.footer}>
          <div className={styles.checkbox}>
            <input
              type="checkbox"
              id="dontShowAgain"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className={styles.checkboxInput}
            />
            <label htmlFor="dontShowAgain" className={styles.checkboxLabel}>
              Don't show tutorial information
            </label>
          </div>
          
          <div className={styles.actions}>
            <button onClick={handleClose} className={styles.startButton}>
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TutorialIntro;